/**
 * <NotificationBell>
 * Stable, click-to-open notification dropdown.
 *
 * Why this lives in its own top-level module:
 *   The previous implementation declared `NotificationBell` as a function *inside*
 *   `Layout`'s body. Because `Layout` has a clock that re-renders every second,
 *   that inner component got a brand-new identity on every render — React
 *   unmounted and remounted the Popover, instantly closing it after ~1 second.
 *   By hoisting NotificationBell here, its identity is stable and the Popover's
 *   internal open state survives parent re-renders.
 *
 * Ownership:
 *   - Owns its own `alerts` state + 30-second polling effect (decoupled from
 *     Layout's clock).
 *   - Owns its own `open` state, controlled (single source of truth).
 *
 * Behavior:
 *   - Single click on the bell toggles the panel.
 *   - Outside click or `Escape` closes it (handled by Radix Popover natively).
 *   - Clicking a notification link closes the panel before navigating.
 *   - First-time loading shows a skeleton; failures show an inline error.
 *   - No hover-only triggers — fully click-driven.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Bell, Package, AlertTriangle, ShieldAlert, CalendarClock, X, Loader2, BellOff,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { alertsAPI } from '../lib/api';
import { formatCurrency as fmtCurrencyIntl } from '../lib/formatters';

const EMPTY = {
    low_stock: [], dues: [], over_credit: [], expiry_items: [], expiry_buckets: {},
    low_stock_count: 0, dues_count: 0, dues_total: 0, over_credit_count: 0,
    expiry_urgent_count: 0, total_count: 0,
};

const NotificationBell = () => {
    const { t, i18n } = useTranslation(['nav', 'common']);
    const lang = (i18n.resolvedLanguage || i18n.language || 'fr').slice(0, 2);
    const formatCurrency = (v) => fmtCurrencyIntl(v, lang);

    const [open, setOpen] = useState(false);
    const [alerts, setAlerts] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAlerts = useCallback(async () => {
        try {
            setError(null);
            const { data } = await alertsAPI.get();
            setAlerts({ ...EMPTY, ...(data || {}) });
        } catch (err) {
            setError(err?.message || 'Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    // Initial fetch + periodic refresh (every 30s) — decoupled from Layout re-renders.
    useEffect(() => {
        fetchAlerts();
        const intv = setInterval(fetchAlerts, 30_000);
        return () => clearInterval(intv);
    }, [fetchAlerts]);

    const closePanel = useCallback(() => setOpen(false), []);

    const hasAlerts = alerts.total_count > 0;
    const countLabel = alerts.total_count === 1
        ? (lang === 'fr' ? 'alerte active' : 'active alert')
        : (lang === 'fr' ? 'alertes actives' : 'active alerts');

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t('nav:bell.alerts', { defaultValue: 'Notifications' })}
                    aria-expanded={open}
                    aria-haspopup="dialog"
                    data-testid="notification-bell"
                    className="relative h-10 w-10 rounded-full bg-white shadow-sm border border-[#E5E5E5] hover:bg-[#F5F5F5] data-[state=open]:bg-[#F5F5F5]"
                >
                    <Bell size={18} className="text-[#1A1A1A]" />
                    {alerts.total_count > 0 && (
                        <span
                            data-testid="notification-count"
                            aria-label={`${alerts.total_count} ${countLabel}`}
                            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E76F51] text-white text-[10px] font-bold flex items-center justify-center"
                        >
                            {alerts.total_count > 99 ? '99+' : alerts.total_count}
                        </span>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent
                role="dialog"
                aria-label={t('nav:bell.alerts', { defaultValue: 'Notifications' })}
                align="end"
                sideOffset={8}
                collisionPadding={16}
                className="w-[calc(100vw-2rem)] sm:w-96 p-0 z-[60] shadow-xl border border-[#E5E5E5]"
                data-testid="notification-panel"
                // Don't auto-close when focus moves to a child link/button — Radix
                // already handles outside-clicks; this just guards against edge cases.
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                {/* Sticky header */}
                <div className="flex items-start justify-between gap-3 p-4 border-b bg-white sticky top-0 z-10">
                    <div className="min-w-0">
                        <h3 className="font-semibold text-[#1A1A1A]" id="notification-panel-title">
                            {t('nav:bell.alerts', { defaultValue: 'Notifications' })}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {loading
                                ? (lang === 'fr' ? 'Chargement…' : 'Loading…')
                                : `${alerts.total_count} ${countLabel}`}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={closePanel}
                        data-testid="notification-close"
                        aria-label={t('common:actions.close', { defaultValue: 'Close' })}
                        className="text-gray-400 hover:text-[#1A1A1A] transition shrink-0 -mr-1 p-1 rounded hover:bg-[#F5F5F5]"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="max-h-[min(70vh,540px)] overflow-y-auto overscroll-contain">
                    {loading && (
                        <div className="p-8 flex flex-col items-center gap-2 text-gray-400" data-testid="notification-loading">
                            <Loader2 size={20} className="animate-spin text-[#C08478]" />
                            <span className="text-xs">{lang === 'fr' ? 'Chargement…' : 'Loading…'}</span>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="p-6 text-center" data-testid="notification-error">
                            <p className="text-sm text-[#E76F51]">{error}</p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={fetchAlerts}
                                className="mt-3"
                            >
                                {lang === 'fr' ? 'Réessayer' : 'Retry'}
                            </Button>
                        </div>
                    )}

                    {!loading && !error && !hasAlerts && (
                        <div className="p-10 text-center" data-testid="notification-empty">
                            <BellOff size={28} className="mx-auto text-gray-300 mb-2" />
                            <p className="text-sm text-gray-400">{t('nav:bell.no_alerts', { defaultValue: 'No notifications' })}</p>
                            <p className="text-[11px] text-gray-300 mt-1">
                                {lang === 'fr' ? 'Vous êtes à jour' : "You're all caught up"}
                            </p>
                        </div>
                    )}

                    {!loading && !error && hasAlerts && (
                        <>
                            {alerts.low_stock_count > 0 && (
                                <NotificationSection
                                    icon={<Package size={16} className="text-[#E9C46A]" />}
                                    title={t('nav:bell.low_stock')}
                                    badgeClass="bg-[#E9C46A]/20 text-[#1A1A1A]"
                                    badgeValue={alerts.low_stock_count}
                                >
                                    {alerts.low_stock.slice(0, 5).map((p) => (
                                        <Link
                                            key={p.id}
                                            to="/products"
                                            onClick={closePanel}
                                            className="flex justify-between items-center text-sm py-2 px-2 -mx-2 rounded hover:bg-[#F5F5F5] transition"
                                        >
                                            <span className="truncate">{p.name}</span>
                                            <span className="text-[#E76F51] font-mono font-bold whitespace-nowrap ml-3">
                                                {p.stock_qty} {p.unit}
                                            </span>
                                        </Link>
                                    ))}
                                    {alerts.low_stock_count > 5 && (
                                        <Link to="/products" onClick={closePanel} className="text-xs text-[#C08478] mt-1 inline-block hover:underline">
                                            {t('nav:bell.view_all')}
                                        </Link>
                                    )}
                                </NotificationSection>
                            )}

                            {alerts.dues_count > 0 && (
                                <NotificationSection
                                    icon={<AlertTriangle size={16} className="text-[#E76F51]" />}
                                    title={t('nav:bell.outstanding_dues')}
                                    badgeClass="bg-[#E76F51]/20 text-[#E76F51]"
                                    badgeValue={alerts.dues_count}
                                >
                                    {alerts.dues.slice(0, 5).map((d) => (
                                        <Link
                                            key={d.id}
                                            to="/dues"
                                            onClick={closePanel}
                                            className="flex justify-between items-center text-sm py-2 px-2 -mx-2 rounded hover:bg-[#F5F5F5] transition"
                                        >
                                            <span className="truncate">{d.company_name || d.invoice_number}</span>
                                            <span className="text-[#E76F51] font-bold whitespace-nowrap ml-3">
                                                {formatCurrency(d.due_amount)}
                                            </span>
                                        </Link>
                                    ))}
                                    <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
                                        {t('common:common.total')}:{' '}
                                        <span className="font-bold text-[#E76F51]">{formatCurrency(alerts.dues_total)}</span>
                                    </div>
                                </NotificationSection>
                            )}

                            {alerts.over_credit_count > 0 && (
                                <NotificationSection
                                    icon={<ShieldAlert size={16} className="text-[#E76F51]" />}
                                    title={t('nav:bell.over_credit')}
                                    badgeClass="bg-[#E76F51] text-white"
                                    badgeValue={alerts.over_credit_count}
                                    testId="over-credit-section"
                                >
                                    {alerts.over_credit.slice(0, 5).map((c) => {
                                        const pct = c.credit_limit && c.credit_limit > 0
                                            ? Math.round((c.outstanding_amount / c.credit_limit) * 100)
                                            : 0;
                                        return (
                                            <Link
                                                key={c.id}
                                                to="/customers"
                                                onClick={closePanel}
                                                className="block py-2 px-2 -mx-2 rounded hover:bg-[#F5F5F5] transition"
                                            >
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="truncate font-medium">{c.company_name}</span>
                                                    <span className="text-[#E76F51] font-mono font-bold text-xs ml-3">
                                                        {pct}%
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center text-[10px] text-gray-500 mt-0.5">
                                                    <span>{formatCurrency(c.outstanding_amount)}</span>
                                                    <span>{t('nav:bell.limit')} {formatCurrency(c.credit_limit)}</span>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                    {alerts.over_credit_count > 5 && (
                                        <Link to="/customers" onClick={closePanel} className="text-xs text-[#C08478] mt-1 inline-block hover:underline">
                                            {t('nav:bell.view_all')}
                                        </Link>
                                    )}
                                </NotificationSection>
                            )}

                            {alerts.expiry_urgent_count > 0 && (
                                <NotificationSection
                                    icon={<CalendarClock size={16} className="text-[#E76F51]" />}
                                    title={t('nav:bell.expiry')}
                                    badgeClass="bg-[#E76F51] text-white"
                                    badgeValue={alerts.expiry_urgent_count}
                                    testId="expiry-section"
                                >
                                    {(alerts.expiry_items || [])
                                        .filter((b) => ['expired', '30', '60'].includes(b.bucket))
                                        .slice(0, 5)
                                        .map((b) => (
                                            <Link
                                                key={b.id}
                                                to="/expiry"
                                                onClick={closePanel}
                                                className="block py-2 px-2 -mx-2 rounded hover:bg-[#F5F5F5] transition"
                                            >
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="truncate font-medium">{b.product_name}</span>
                                                    <span className={`text-[10px] font-bold whitespace-nowrap ml-3 ${b.bucket === 'expired' ? 'text-[#7F1D1D]' : 'text-[#E76F51]'}`}>
                                                        {b.bucket === 'expired'
                                                            ? (lang === 'fr' ? `expiré il y a ${Math.abs(b.days_to_expiry)}j` : `expired ${Math.abs(b.days_to_expiry)}d ago`)
                                                            : (lang === 'fr' ? `${b.days_to_expiry}j` : `${b.days_to_expiry}d`)}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center text-[10px] text-gray-500">
                                                    <span className="font-mono">{b.batch_number}</span>
                                                    <span>{b.quantity_remaining} {lang === 'fr' ? 'unités' : 'units'}</span>
                                                </div>
                                            </Link>
                                        ))}
                                    <Link to="/expiry" onClick={closePanel} className="text-xs text-[#C08478] mt-1 inline-block hover:underline">
                                        {t('nav:bell.open_expiry')}
                                    </Link>
                                </NotificationSection>
                            )}
                        </>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
};

const NotificationSection = ({ icon, title, badgeClass, badgeValue, children, testId }) => (
    <section className="p-4 border-b last:border-b-0" data-testid={testId}>
        <div className="flex items-center gap-2 mb-2">
            {icon}
            <span className="font-medium text-sm">{title}</span>
            <Badge className={`${badgeClass} text-xs`}>{badgeValue}</Badge>
        </div>
        {children}
    </section>
);

export default NotificationBell;
